import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Card,
  CircularProgress,
  CardContent,
  CardMedia,
  Grid,
  TextField,
  InputAdornment,
  Chip,
  Box,
  Button,
  Rating,
  Avatar,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Tab,
  Tabs,
  Snackbar
} from '@mui/material';
import {
  Search,
  LocationOn,
  Star,
  Verified,
  Phone,
  Email,
  FilterList,
  Construction,
  Plumbing,
  ElectricalServices,
  Handyman,
  Carpenter,
  CheckCircle,
  Warning,
  WorkHistory,
  AccountBalanceWallet,
  ReportProblem,
  ThumbUp,
  Message,
  LocalShipping,
  Brush,
  Engineering,
  WhatsApp,
  CalendarToday,
  TrendingUp,
  Groups,
  Clear,
  RequestQuote,
  NavigateBefore,
  NavigateNext,
  Close,
  PhotoLibrary,
  Share,
  Favorite,
  FavoriteBorder
} from '@mui/icons-material';
import { brand, bg, border } from '../theme/colors';
import currencyService from '../services/currencyService';
import { createAuthenticatedService } from '../utils/apiClient';
import { ContractorService } from '../services/contractorService';
import { useAuth } from '../contexts/AuthContext';
import Seo from '../components/Seo';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import AIContractorHiringDialog from '../components/contractors/AIContractorHiringDialog';
import useSavedContractors from '../hooks/useSavedContractors';
import ContractorSearchFilters from '../components/ContractorSearchFilters';
import DialogCloseButton from '../components/common/DialogCloseButton';
import { shareProfileLink } from '../utils/shareLink';
import TrustLine from '../components/contractors/TrustLine';

const ArtisansPage = () => {
  const { apiClient, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [artisans, setArtisans] = useState([]); // Start with empty array
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');
  const [sortBy, setSortBy] = useState('rating');
  const [selectedArtisan, setSelectedArtisan] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [enhancedView, setEnhancedView] = useState(false); // Toggle for enhanced view - default to Standard View
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState({});
  // Portfolio photo viewer: { title, images: [url...], index }
  const [gallery, setGallery] = useState(null);
  const [showSavedOnly, setShowSavedOnly] = useState(false);
  const { isClient, isSaved, toggle: toggleSaved, saved: savedContractors } = useSavedContractors();

  // If the last saved contractor is removed while the filter is on, drop the
  // filter too — otherwise the grid is stuck empty with no chip to clear it.
  useEffect(() => {
    if (showSavedOnly && savedContractors.length === 0) {
      setShowSavedOnly(false);
    }
  }, [showSavedOnly, savedContractors.length]);
  const [shareToastOpen, setShareToastOpen] = useState(false);
  
  // Contact contractor state (conversational AI dialog)
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [selectedContractorId, setSelectedContractorId] = useState(null);
  const projectId = searchParams.get('projectId');
  const projectTitle = searchParams.get('projectTitle');
  const milestoneId = searchParams.get('milestoneId');
  const milestoneName = searchParams.get('milestoneName');
  
  // Load contractors with metrics
  useEffect(() => {
    const loadContractors = async () => {
      setLoading(true);
      
      try {
        // Call API directly without requiring apiClient
        const response = await api.get('/contractors');
        const data = response.data;
        
        if (data.success && data.data && Array.isArray(data.data)) {
          console.log('Loaded contractors:', data.data.length);
          
          // Transform backend data to match frontend format
          const transformedContractors = data.data.map(contractor => ({
            id: contractor.id,
            name: contractor.name,
            type: contractor.type || 'Contractor',
            avatar: contractor.avatar,
            rating: contractor.rating || 0,
            reviewCount: contractor.reviewCount || 0,
            verified: contractor.verified || false,
            verifiedBy: ['Eqaya Platform'],
            location: contractor.location || 'Harare',
            specialties: contractor.specialties || [],
            experience: contractor.experience || 'Not specified',
            completedProjects: 0,
            onTimeRate: contractor.onTimeRate || 0, // From API - calculated from project/milestone deadlines
            responseRate: contractor.responseRate || 0, // From API - calculated from messages/quote requests
            phone: contractor.phone,
            whatsapp: contractor.phone,
            description: contractor.bio || 'Professional contractor',
            availability: 'Available',
            languages: contractor.languages || ['English'],
            team: 'Individual',
            disputes: 0,
            resolvedDisputes: 0,
            redFlags: [],
            trustScore: contractor.trustScore || 0, // From API - calculated from rating, on-time, response, disputes, verification
            recentReviews: [],
            portfolio: contractor.portfolio_items || [], // Portfolio items from API
            trust: contractor.trust || null, // evidence for the "Why Eqaya trusts" line
            priceTransparency: {}
          }));
          
          setArtisans(transformedContractors);
        } else {
          console.log('No contractor data from API');
          setArtisans([]);
        }
      } catch (error) {
        console.error('Failed to load contractors:', error);
        setArtisans([]);
      } finally {
        setLoading(false);
      }
    };

    loadContractors();
  }, []);

  // Fix for ResizeObserver error
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Load user projects for quote wizard
  useEffect(() => {
    const loadProjects = async () => {
      try {
        await api.get('/projects');

        // Projects loaded - not needed for conversational flow
        // if (response.data.success) {
        //   const data = response.data;
        // }
      } catch (error) {
        console.error('Failed to load projects:', error);
      }
    };

    loadProjects();
  }, []);
  
  // Auto-open quote dialog if contractorId is in URL (from FindContractorsDialog)
  useEffect(() => {
    const contractorIdParam = searchParams.get('contractorId');
    if (contractorIdParam && !loading && artisans.length > 0) {
      setSelectedContractorId(contractorIdParam);
      setContactDialogOpen(true);
      // Clean up URL param
      searchParams.delete('contractorId');
      setSearchParams(searchParams, { replace: true });
    }
  }, [loading, artisans.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced tab change to prevent ResizeObserver errors
  const handleTabChange = useCallback((e, newValue) => {
    // Small delay to prevent ResizeObserver loop error
    setTimeout(() => {
      setActiveTab(newValue);
    }, 10);
  }, []);

  // Get unique types and locations (only if artisans exist)
  const artisanTypes = artisans.length > 0 ? ['all', ...new Set(artisans.map(a => a.type))] : ['all'];
  const locations = artisans.length > 0 ? ['all', ...new Set(artisans.map(a => a.location.split(',')[1]?.trim() || a.location))] : ['all'];

  // Filter and sort artisans (driven by the advanced `filters` panel + search/sort).
  const filteredArtisans = artisans
    .filter(artisan => {
      const term = searchTerm.trim().toLowerCase();
      const matchesSearch = !term ||
        artisan.name.toLowerCase().includes(term) ||
        artisan.type.toLowerCase().includes(term) ||
        artisan.specialties.some(s => s.toLowerCase().includes(term));
      const matchesType = !filters.contractorType ||
        (artisan.type || '').toLowerCase().includes(filters.contractorType.toLowerCase());
      const matchesLocation = !filters.location ||
        (artisan.location || '').toLowerCase().includes(filters.location.toLowerCase());
      const matchesSkills = !filters.skills || filters.skills.length === 0 ||
        filters.skills.some(skill => artisan.specialties.some(s => s.toLowerCase() === skill.toLowerCase()));
      const matchesRating = !filters.minRating || (artisan.rating || 0) >= filters.minRating;
      const matchesSaved = !showSavedOnly || isSaved(artisan.id);
      return matchesSearch && matchesType && matchesLocation && matchesSkills && matchesRating && matchesSaved;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'rating': return b.rating - a.rating;
        case 'projects': return b.completedProjects - a.completedProjects;
        case 'reliability': return b.onTimeRate - a.onTimeRate;
        default: return 0;
      }
    });

  const handleClearFilters = () => {
    setSearchTerm('');
    setSortBy('rating');
    setFilters({});
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const hasActiveFilters = searchTerm !== '' || sortBy !== 'rating' ||
    !!filters.contractorType || !!filters.location ||
    (filters.skills && filters.skills.length > 0) ||
    (filters.minRating && filters.minRating > 0);

  const getTypeIcon = (type) => {
    const iconMap = {
      'Builder': <Construction />,
      'Electrician': <ElectricalServices />,
      'Plumber': <Plumbing />,
      'Painter': <Brush />,
      'Carpenter': <Carpenter />,
      'Tiler': <Construction />,
      'Project Manager': <Engineering />
    };
    return iconMap[type] || <Handyman />;
  };

  const getReliabilityColor = (rate) => {
    if (rate >= 90) return 'success';
    if (rate >= 75) return 'warning';
    return 'error';
  };

  const handleViewDetails = (artisan) => {
    setSelectedArtisan(artisan);
    // Small delay to prevent ResizeObserver loop error
    setTimeout(() => {
      setDetailsDialog(true);
    }, 10);
  };

  // Collect every usable photo for a portfolio item (arrays may arrive as a real
  // array or a comma-separated string), de-duplicated and in display order.
  const collectPortfolioImages = (item) => {
    const toList = (v) => (Array.isArray(v) ? v : (typeof v === 'string' && v ? v.split(',').map((s) => s.trim()) : []));
    const all = [
      item.thumbnail_url,
      item.cover_image_url,
      ...toList(item.after_images),
      ...toList(item.before_images),
    ].filter(Boolean);
    return [...new Set(all)];
  };

  const openGallery = (item) => {
    const images = collectPortfolioImages(item);
    if (!images.length) return;
    setGallery({ title: item.project_title || 'Project', images, index: 0, contractorId: selectedArtisan?.id });
  };

  // Every contractor has a public profile — share the /share/ URL so link
  // previews (WhatsApp etc.) get server-rendered OG tags; it redirects
  // humans to the public profile.
  const handleShareContractor = async (contractorId, name) => {
    const result = await shareProfileLink({
      path: `/share/contractor/${contractorId}`,
      title: `${name} on Eqaya`
    });
    if (result === 'copied') setShareToastOpen(true);
  };

  const stepGallery = (delta) => setGallery((g) => (
    g ? { ...g, index: (g.index + delta + g.images.length) % g.images.length } : g
  ));

  // Opens the quote wizard; pass a contractor object to pre-seed that contractor.
  const handleContactContractor = (contractor = null) => {
    if (!user) {
      navigate('/login', { state: { from: '/contractors' } });
      return;
    }

    const authProvider = user.authProvider || user.auth_provider || 'local';
    const emailVerified = user.emailVerified || user.email_verified;
    if (authProvider === 'local' && !emailVerified) {
      navigate('/verify-email', { state: { from: { pathname: '/contractors' } } });
      return;
    }

    setSelectedContractorId(contractor?.id || null);
    setContactDialogOpen(true);
  };

  // Every portfolio photo across ALL of a contractor's projects, de-duped.
  // Drives the card hero (images[0]), the "N photos" badge (count), and the
  // gallery (the full list) — so the badge count always matches what opens.
  const getContractorHero = (artisan) => {
    const items = artisan.portfolio || [];
    const images = [...new Set(items.flatMap((item) => collectPortfolioImages(item)))];
    return { image: images[0] || null, count: images.length, images };
  };

  const getTrustScore = (artisan) => {
    // Use pre-calculated trust score from backend, or calculate if missing
    if (artisan.trustScore) {
      return artisan.trustScore;
    }
    
    // Fallback calculation for mock data
    const ratingScore = artisan.rating * 20; // Max 100
    const completionScore = artisan.onTimeRate; // Max 100
    const disputeScore = artisan.disputes === 0 ? 100 : (artisan.resolvedDisputes / artisan.disputes) * 100;
    const responseScore = artisan.responseRate; // Max 100
    
    return Math.round((ratingScore + completionScore + disputeScore + responseScore) / 4);
  };

  // Show loading state
  if (loading || !mounted) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading contractors...</Typography>
      </Container>
    );
  }
  
  // Show empty state when no contractors
  if (!loading && artisans.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
          Find Contractors
        </Typography>
        
        <Card sx={{ mt: 4, textAlign: 'center', py: 8 }}>
          <CardContent>
            <Construction sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              No Contractors Available Yet
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              We're building our network of verified contractors. Check back soon!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Contractors can register on the platform to join our growing community.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Seo
        title="Find Verified Contractors in Zimbabwe"
        description="Browse verified builders, electricians, plumbers, painters and more. Compare portfolios and reviews, then request quotations — all on Eqaya."
        canonicalPath="/contractors"
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Find Contractors
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => handleContactContractor()}
          sx={{
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            color: 'white',
            fontWeight: 600,
            px: 4,
            py: 1.5,
            '&:hover': {
              background: 'linear-gradient(135deg, #0f8578 0%, #2dd46a 100%)',
              transform: 'translateY(-2px)',
              boxShadow: 4
            },
            transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), background 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          Request Quotes
        </Button>
      </Box>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Eqaya Accountability System:</strong> All contractors are rated based on completed projects, 
          on-time delivery, and dispute resolution. Look for verified badges and check recent reviews from previous clients.
        </Typography>
      </Alert>

      {projectId && projectTitle && (
        <Alert severity="info" icon={false} sx={{ mb: 3, bgcolor: bg.selected, border: `1px solid ${border.light}` }}>
          <Typography variant="body2" color="text.primary">
            <strong>How hiring works:</strong> Request a quote from a contractor so they can review your project scope and propose their price. Once you accept their quote, they're officially hired and you can start working together.
          </Typography>
        </Alert>
      )}

      {/* Search Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search contractors, skills..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                label="Sort By"
              >
                <MenuItem value="rating">Highest Rated</MenuItem>
                <MenuItem value="projects">Most Experienced</MenuItem>
                <MenuItem value="reliability">Most Reliable</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant={showFilters ? 'contained' : 'outlined'}
              startIcon={<FilterList />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{ height: '56px' }}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Advanced Filters Row */}
      {showFilters && (
        <Box sx={{ mb: 3 }}>
          <ContractorSearchFilters
            onFilterChange={handleFilterChange}
            initialFilters={filters}
          />
        </Box>
      )}

      {/* Results Summary */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {loading ? 'Loading...' : `${filteredArtisans.length} contractors found`}
        </Typography>
        {isClient && savedContractors.length > 0 && (
          <Chip
            icon={<Favorite sx={{ fontSize: 16 }} />}
            label={`Saved (${savedContractors.length})`}
            color={showSavedOnly ? 'primary' : 'default'}
            onClick={() => setShowSavedOnly((v) => !v)}
          />
        )}
      </Box>

      {/* Standard Artisan Cards */}
      <Grid container spacing={3}>
        {filteredArtisans.map((artisan) => {
          const hero = getContractorHero(artisan);
          return (
          <Grid item xs={12} md={6} key={artisan.id}>
            <Card
              onClick={() => navigate(`/contractors/${artisan.id}`)}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
            >
              {/* Portfolio photo hero (falls back to a branded placeholder) */}
              {hero.image ? (
                <Box
                  sx={{ position: 'relative', cursor: 'pointer' }}
                  onClick={(e) => { e.stopPropagation(); if (hero.images.length) setGallery({ title: `${artisan.name} — Portfolio`, images: hero.images, index: 0, contractorId: artisan.id, contractorName: artisan.name }); }}
                >
                  <CardMedia
                    component="img"
                    image={hero.image}
                    alt={`${artisan.name} portfolio`}
                    sx={{ height: 180, objectFit: 'cover' }}
                  />
                  {hero.count > 0 && (
                    <Chip
                      icon={<PhotoLibrary sx={{ fontSize: 14 }} />}
                      label={`${hero.count} photo${hero.count > 1 ? 's' : ''}`}
                      size="small"
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        left: 8,
                        bgcolor: 'rgba(0,0,0,0.65)',
                        color: '#fff',
                        fontWeight: 600,
                        '& .MuiChip-icon': { color: '#fff' },
                      }}
                    />
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    height: 180,
                    bgcolor: brand.primary,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'rgba(255,255,255,0.92)',
                  }}
                >
                  <Avatar sx={{ width: 48, height: 48, bgcolor: 'rgba(255,255,255,0.18)', color: '#fff' }}>
                    {getTypeIcon(artisan.type)}
                  </Avatar>
                  <Typography variant="caption" sx={{ mt: 1, color: 'rgba(255,255,255,0.88)' }}>
                    Portfolio coming soon
                  </Typography>
                </Box>
              )}

              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="h6">{artisan.name}</Typography>
                      {artisan.verified && (
                        <Chip
                          icon={<Verified sx={{ fontSize: 14 }} />}
                          label="Verified"
                          size="small"
                          sx={{
                            bgcolor: `${brand.primary}14`,
                            color: brand.primary,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 22,
                            '& .MuiChip-icon': { color: brand.primary },
                          }}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {artisan.type} • {artisan.experience}
                    </Typography>
                    <TrustLine facts={artisan.trust} compact />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                    {artisan.rating > 0 ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Rating value={artisan.rating} precision={0.1} readOnly size="small" />
                        <Typography variant="body2" fontWeight="bold">
                          {artisan.rating}
                        </Typography>
                      </Box>
                    ) : (
                      <Chip label="New" size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                      {isClient && (
                        <IconButton
                          size="small"
                          aria-label={isSaved(artisan.id) ? `Remove ${artisan.name} from saved` : `Save ${artisan.name} for later`}
                          onClick={(e) => { e.stopPropagation(); toggleSaved(artisan.id); }}
                          sx={{ color: isSaved(artisan.id) ? 'error.main' : 'text.secondary' }}
                        >
                          {isSaved(artisan.id) ? <Favorite sx={{ fontSize: 18 }} /> : <FavoriteBorder sx={{ fontSize: 18 }} />}
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        aria-label={`Share ${artisan.name}'s profile link`}
                        onClick={(e) => { e.stopPropagation(); handleShareContractor(artisan.id, artisan.name); }}
                        sx={{ color: 'text.secondary' }}
                      >
                        <Share sx={{ fontSize: 18 }} />
                      </IconButton>
                      <Typography
                        variant="body2"
                        color="primary"
                        sx={{
                          cursor: 'pointer',
                          fontWeight: 500,
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={(e) => { e.stopPropagation(); navigate(`/contractors/${artisan.id}`); }}
                      >
                        View Profile →
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Trust strip: location + availability */}
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<LocationOn />}
                    label={artisan.location}
                    size="small"
                  />
                  <Chip
                    icon={<CheckCircle />}
                    label={artisan.availability}
                    size="small"
                    color={artisan.availability === 'Available' ? 'success' : 'warning'}
                  />
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    mb: 2,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {artisan.description}
                </Typography>

                {artisan.redFlags.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="caption">
                      {artisan.redFlags[0]}
                    </Typography>
                  </Alert>
                )}

                {/* Action Buttons */}
                <Grid container spacing={1} sx={{ mt: 'auto' }}>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      startIcon={<Message />}
                      onClick={(e) => { e.stopPropagation(); navigate(`/messages?contractorId=${artisan.id}${projectId ? `&projectId=${projectId}&projectTitle=${encodeURIComponent(projectTitle || '')}` : ''}`); }}

                      sx={{
                        borderColor: brand.primary,
                        color: brand.primary,
                        fontWeight: 600,
                        '&:hover': {
                          borderColor: brand.primaryDark,
                          backgroundColor: 'rgba(13, 148, 136, 0.08)',
                        },
                        transition: 'border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    >
                      Message
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      size="small"
                      startIcon={<RequestQuote />}
                      onClick={(e) => { e.stopPropagation(); handleContactContractor(artisan); }}
                      sx={{
                        background: brand.gradient,
                        color: 'white',
                        fontWeight: 600,
                        '&:hover': {
                          background: brand.gradientHover,
                        },
                        transition: 'background 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    >
                      Request Quote
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          );
        })}
      </Grid>

      {/* Artisan Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => {
          // Small delay to prevent ResizeObserver loop error
          setTimeout(() => setDetailsDialog(false), 10);
        }}
        maxWidth="md"
        fullWidth
        TransitionProps={{
          onExited: () => {
            // Clean up when dialog closes
            setSelectedArtisan(null);
            setActiveTab(0);
          }
        }}
      >
        <DialogCloseButton onClick={() => setTimeout(() => setDetailsDialog(false), 10)} />
        {selectedArtisan && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.main' }}>
                    {getTypeIcon(selectedArtisan.type)}
                  </Avatar>
                  <Box>
                    <Typography variant="h6">
                      {selectedArtisan.name}
                      {selectedArtisan.verified && (
                        <Verified color="primary" fontSize="small" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedArtisan.type} • {selectedArtisan.location}
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  label={`Trust Score: ${getTrustScore(selectedArtisan)}%`} 
                  color="primary"
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
                <Tab label="Overview" />
                <Tab label="Portfolio" />
                <Tab label="Reviews" />
                <Tab label="Work History" />
              </Tabs>

              {activeTab === 0 && (
                <Box>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>Contact Information</Typography>
                        <List dense>
                          <ListItem>
                            <ListItemIcon><CalendarToday /></ListItemIcon>
                            <ListItemText primary={selectedArtisan.availability} />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><Message /></ListItemIcon>
                            <ListItemText 
                              primary="Send Message" 
                              secondary="Communicate via messaging platform"
                            />
                          </ListItem>
                        </List>
                        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                          <Button
                            variant="outlined"
                            startIcon={<Message />}
                            onClick={() => {
                              navigate(`/messages?contractorId=${selectedArtisan.id}${projectId ? `&projectId=${projectId}&projectTitle=${encodeURIComponent(projectTitle || '')}` : ''}`);
                              setDetailsDialog(false);
                            }}
                            fullWidth
                          >
                            {projectId ? 'Message About Project' : 'Open Messaging'}
                          </Button>
                        </Box>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>Performance Metrics</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Completion Rate
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ flexGrow: 1, bgcolor: 'grey.200', height: 8, borderRadius: 1 }}>
                                <Box 
                                  sx={{ 
                                    width: `${selectedArtisan.onTimeRate}%`, 
                                    bgcolor: getReliabilityColor(selectedArtisan.onTimeRate) + '.main',
                                    height: '100%',
                                    borderRadius: 1
                                  }} 
                                />
                              </Box>
                              <Typography variant="body2">{selectedArtisan.onTimeRate}%</Typography>
                            </Box>
                          </Box>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Response Rate
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ flexGrow: 1, bgcolor: 'grey.200', height: 8, borderRadius: 1 }}>
                                <Box 
                                  sx={{ 
                                    width: `${selectedArtisan.responseRate}%`, 
                                    bgcolor: 'primary.main',
                                    height: '100%',
                                    borderRadius: 1
                                  }} 
                                />
                              </Box>
                              <Typography variant="body2">{selectedArtisan.responseRate}%</Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>About</Typography>
                        <Typography variant="body2" paragraph>
                          {selectedArtisan.description}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Languages:</strong> {selectedArtisan.languages.join(', ')}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Team Size:</strong> {selectedArtisan.team}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {activeTab === 1 && (
                <Box>
                  {/* Portfolio Tab */}
                  {selectedArtisan.portfolio && selectedArtisan.portfolio.length > 0 ? (
                    <Grid container spacing={2}>
                      {selectedArtisan.portfolio.map((item) => {
                        // Collect every uploaded photo so a thumbnail-less project still
                        // shows its after/before images, and the client can page through all.
                        const images = collectPortfolioImages(item);
                        const mainImage = images[0] || null;
                        const photoCount = images.length;
                        return (
                        <Grid item xs={12} sm={6} key={item.id}>
                          <Card
                            sx={{
                              height: '100%',
                              cursor: photoCount ? 'pointer' : 'default',
                              transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                              '&:hover': photoCount ? { boxShadow: 4, transform: 'translateY(-2px)' } : {},
                            }}
                            onClick={() => photoCount && openGallery(item)}
                          >
                            {mainImage && (
                              <Box sx={{ position: 'relative' }}>
                                <CardMedia
                                  component="img"
                                  height="160"
                                  image={mainImage}
                                  alt={item.project_title}
                                  sx={{ objectFit: 'cover' }}
                                />
                                {photoCount > 1 && (
                                  <Chip
                                    icon={<PhotoLibrary sx={{ color: '#fff !important' }} />}
                                    label={`${photoCount} photos`}
                                    size="small"
                                    sx={{
                                      position: 'absolute', bottom: 8, right: 8,
                                      bgcolor: 'rgba(0,0,0,0.6)', color: '#fff',
                                      '& .MuiChip-icon': { color: '#fff' },
                                    }}
                                  />
                                )}
                              </Box>
                            )}
                            <CardContent>
                              <Typography variant="h6" gutterBottom>
                                {item.project_title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {item.description}
                              </Typography>
                              {item.project_type && (
                                <Chip
                                  label={item.project_type}
                                  size="small"
                                  sx={{ mr: 1, mb: 1 }}
                                />
                              )}
                              {item.city && (
                                <Chip
                                  icon={<LocationOn />}
                                  label={item.city}
                                  size="small"
                                  sx={{ mb: 1 }}
                                />
                              )}
                              {photoCount > 0 && (
                                <Typography
                                  variant="caption"
                                  color="primary"
                                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, fontWeight: 600 }}
                                >
                                  <PhotoLibrary fontSize="inherit" />
                                  {photoCount > 1 ? `View all ${photoCount} photos` : 'View photo'}
                                </Typography>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                        );
                      })}
                    </Grid>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <Construction sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        No Portfolio Items Yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        This contractor hasn't added any portfolio projects yet.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {activeTab === 2 && (
                <Box>
                  {selectedArtisan.recentReviews.map((review) => (
                    <Paper key={review.id} sx={{ p: 2, mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Box>
                          <Typography variant="subtitle2">
                            {review.reviewer}
                            {review.verified && (
                              <Verified color="primary" fontSize="small" sx={{ ml: 1 }} />
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {review.project} • {review.date}
                          </Typography>
                        </Box>
                        <Rating value={review.rating} readOnly size="small" />
                      </Box>
                      <Typography variant="body2">
                        {review.comment}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              )}

              {activeTab === 3 && (
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="primary">
                          {selectedArtisan.completedProjects}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Completed Projects
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="primary">
                          {selectedArtisan.disputes}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Disputes
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main">
                          {selectedArtisan.resolvedDisputes}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Resolved Disputes
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailsDialog(false)}>Close</Button>
              <Button
                startIcon={<Share />}
                onClick={() => handleShareContractor(selectedArtisan.id, selectedArtisan.name)}
              >
                Share
              </Button>
              <Button
                onClick={() => {
                  setDetailsDialog(false);
                  navigate(`/contractors/${selectedArtisan.id}`);
                }}
              >
                View Full Profile
              </Button>
              <Button
                variant="contained"
                startIcon={<Message />}
                onClick={() => {
                  navigate(`/messages?contractorId=${selectedArtisan.id}`);
                  setDetailsDialog(false);
                }}
              >
                Contact via Eqaya
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Conversational AI Contractor Hiring Dialog */}
      <AIContractorHiringDialog
        open={contactDialogOpen}
        contractorId={selectedContractorId}
        projectId={projectId}
        milestoneId={milestoneId}
        milestoneName={milestoneName}
        onClose={() => { setContactDialogOpen(false); setSelectedContractorId(null); }}
        onComplete={(data) => {
          console.log('Quote requests sent to:', data.contractors);
          setContactDialogOpen(false);
          setSelectedContractorId(null);
        }}
      />

      {/* Portfolio photo viewer — click a project to page through all its photos */}
      <Dialog
        open={Boolean(gallery)}
        onClose={() => setGallery(null)}
        maxWidth="md"
        fullWidth
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight') stepGallery(1);
          else if (e.key === 'ArrowLeft') stepGallery(-1);
        }}
      >
        {gallery && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
              <Box>
                <Typography variant="h6" component="div">{gallery.title}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Photo {gallery.index + 1} of {gallery.images.length}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {gallery.contractorId && (
                  <Button
                    size="small"
                    startIcon={<Share />}
                    onClick={() => handleShareContractor(gallery.contractorId, gallery.contractorName || gallery.title)}
                  >
                    Share
                  </Button>
                )}
                <IconButton onClick={() => setGallery(null)} aria-label="Close photo viewer">
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <Box sx={{ position: 'relative', bgcolor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320 }}>
                <Box
                  component="img"
                  src={gallery.images[gallery.index]}
                  alt={`${gallery.title} — photo ${gallery.index + 1}`}
                  sx={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', display: 'block' }}
                />
                {gallery.images.length > 1 && (
                  <>
                    <IconButton
                      onClick={() => stepGallery(-1)}
                      aria-label="Previous photo"
                      sx={{ position: 'absolute', left: 8, color: '#fff', bgcolor: 'rgba(0,0,0,0.4)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                    >
                      <NavigateBefore fontSize="large" />
                    </IconButton>
                    <IconButton
                      onClick={() => stepGallery(1)}
                      aria-label="Next photo"
                      sx={{ position: 'absolute', right: 8, color: '#fff', bgcolor: 'rgba(0,0,0,0.4)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                    >
                      <NavigateNext fontSize="large" />
                    </IconButton>
                  </>
                )}
              </Box>
              {gallery.images.length > 1 && (
                <Box sx={{ display: 'flex', gap: 1, p: 1, overflowX: 'auto', justifyContent: 'center', flexWrap: 'wrap' }}>
                  {gallery.images.map((img, i) => (
                    <Box
                      key={img + i}
                      component="img"
                      src={img}
                      alt={`thumbnail ${i + 1}`}
                      onClick={() => setGallery((g) => ({ ...g, index: i }))}
                      sx={{
                        width: 64, height: 48, objectFit: 'cover', borderRadius: 1, cursor: 'pointer',
                        border: i === gallery.index ? '2px solid' : '2px solid transparent',
                        borderColor: i === gallery.index ? 'primary.main' : 'transparent',
                        opacity: i === gallery.index ? 1 : 0.7,
                      }}
                    />
                  ))}
                </Box>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>

      <Snackbar
        open={shareToastOpen}
        autoHideDuration={3000}
        onClose={() => setShareToastOpen(false)}
        message="Profile link copied — share it with anyone"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
};

export default ArtisansPage;
